/**
 * Imagem de divulgação do miolo.
 *
 * O diálogo mostra a cena **de verdade** (a mesma que o servidor vai fotografar),
 * deixa escolher formato, fundo, cena e assinatura, e baixa o PNG. É o material
 * de venda do cliente: ele publica a foto da agenda aberta sem precisar fotografar
 * nada e sem abrir editor de imagem.
 *
 * A prévia é o próprio componente da cena, reduzido — não uma imitação. Se a
 * prévia e o arquivo divergissem, o cliente descobriria depois de publicar.
 */

import { useState } from 'react';
import {
  CENAS_MOCKUP,
  DESCRICAO_CENA_MOCKUP,
  DESCRICAO_FUNDO_MOCKUP,
  FORMATOS_MOCKUP,
  FUNDOS_MOCKUP,
  MAXIMO_ASSINATURA_MOCKUP,
  MEDIDAS_MOCKUP,
  ROTULO_CENA_MOCKUP,
  ROTULO_FUNDO_MOCKUP,
  type ConfigPlanner,
  type DocumentoMiolo,
  type OpcoesMockup,
  type Paleta,
} from '@miolos/core';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Campo } from '@/components/ui/label';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { CenaMockup, fundoDoMockup, paginasDaCena } from '@/mockup/CenaMockup';
import { useLargura } from '@/impressao/MolduraPrevia';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  mioloId: string;
  nomeDoMiolo: string;
  /** Assinatura sugerida (o nome do cliente, o @ da gráfica...). */
  assinaturaSugerida: string | null;
  config: ConfigPlanner;
  paleta: Paleta;
  documento: DocumentoMiolo;
}

export function DialogoMockup({
  aberto,
  aoFechar,
  mioloId,
  nomeDoMiolo,
  assinaturaSugerida,
  config,
  paleta,
  documento,
}: Props) {
  const [opcoes, setOpcoes] = useState<OpcoesMockup>({
    formato: 'QUADRADO',
    fundo: 'MESA',
    cena: 'CAPA_E_DIA',
    assinatura: assinaturaSugerida,
  });
  const [montando, setMontando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { ref, largura } = useLargura<HTMLDivElement>();
  const medida = MEDIDAS_MOCKUP[opcoes.formato];
  const fator = largura > 0 ? largura / medida.larguraPx : 0;

  const baixar = async () => {
    setErro(null);
    setMontando(true);
    try {
      await api.baixarMockup(mioloId, nomeDoMiolo, {
        formato: opcoes.formato,
        fundo: opcoes.fundo,
        cena: opcoes.cena,
        assinatura: opcoes.assinatura?.trim() ? opcoes.assinatura.trim() : null,
      });
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível montar a imagem.');
    } finally {
      setMontando(false);
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={(valor) => (valor ? undefined : aoFechar())}>
      <DialogContent
        titulo="Imagem de divulgação"
        descricao="Uma foto da agenda aberta, com a espiral, para publicar. O arquivo sai no tamanho da rede social — o PDF continua sendo o arquivo da gráfica."
        className="max-w-2xl"
      >
        <div className="space-y-4">
          {/* A prévia é a cena de verdade, reduzida ao espaço do diálogo. */}
          <div
            ref={ref}
            className="overflow-hidden rounded-lg border border-border bg-secondary/40"
            style={{ height: largura > 0 ? medida.alturaPx * fator : undefined }}
          >
            {largura > 0 ? (
              <CenaMockup
                config={config}
                paleta={paleta}
                paginas={paginasDaCena(documento, opcoes.cena)}
                opcoes={opcoes}
                fator={fator}
              />
            ) : null}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {FORMATOS_MOCKUP.map((formato) => (
              <CartaoOpcao
                key={formato}
                selecionado={opcoes.formato === formato}
                titulo={MEDIDAS_MOCKUP[formato].rotulo}
                descricao={`${MEDIDAS_MOCKUP[formato].larguraPx} × ${MEDIDAS_MOCKUP[formato].alturaPx} px`}
                aoSelecionar={() => setOpcoes((atual) => ({ ...atual, formato }))}
              />
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {CENAS_MOCKUP.map((cena) => (
              <CartaoOpcao
                key={cena}
                selecionado={opcoes.cena === cena}
                titulo={ROTULO_CENA_MOCKUP[cena]}
                descricao={DESCRICAO_CENA_MOCKUP[cena]}
                aoSelecionar={() => setOpcoes((atual) => ({ ...atual, cena }))}
              />
            ))}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Fundo</p>
            <div className="flex flex-wrap gap-2">
              {FUNDOS_MOCKUP.map((fundo) => {
                const amostra = fundoDoMockup(fundo, paleta);
                return (
                  <button
                    key={fundo}
                    type="button"
                    aria-pressed={opcoes.fundo === fundo}
                    onClick={() => setOpcoes((atual) => ({ ...atual, fundo }))}
                    title={DESCRICAO_FUNDO_MOCKUP[fundo]}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs transition-colors',
                      opcoes.fundo === fundo
                        ? 'border-primary bg-primary/[0.06] font-medium text-foreground'
                        : 'border-border text-muted-foreground hover:border-primary/40',
                    )}
                  >
                    <span
                      aria-hidden
                      className="size-5 rounded border border-black/10"
                      style={{ background: amostra.css }}
                    />
                    {ROTULO_FUNDO_MOCKUP[fundo]}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {DESCRICAO_FUNDO_MOCKUP[opcoes.fundo]}
            </p>
          </div>

          <Campo
            rotulo="Assinatura (opcional)"
            dica="Sua marca no pé da imagem: nome da gráfica, @ do Instagram, telefone."
          >
            <input
              type="text"
              value={opcoes.assinatura ?? ''}
              maxLength={MAXIMO_ASSINATURA_MOCKUP}
              placeholder="Ateliê da Ana · @ateliedaana"
              onChange={(evento) =>
                setOpcoes((atual) => ({ ...atual, assinatura: evento.target.value || null }))
              }
              className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm sm:h-9"
            />
          </Campo>

          {erro ? <p className="text-sm text-destructive">{erro}</p> : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="fantasma" onClick={aoFechar} disabled={montando}>
              Fechar
            </Button>
            <Button onClick={() => void baixar()} disabled={montando}>
              {montando ? <Loader2 className="animate-spin" /> : null}
              {montando ? 'Montando a imagem...' : 'Baixar imagem (PNG)'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
